<template>
  <div
    class="tw:flex tw:items-center tw:gap-2"
    :class="
      disabled ? 'tw:cursor-not-allowed tw:opacity-70' : 'tw:cursor-pointer'
    "
    data-testid="base-toggle"
    v-bind="wrapperAttrs"
    @click="toggle"
  >
    <button
      :aria-checked="modelValue"
      :aria-controls="ariaControls"
      :aria-disabled="disabled"
      :aria-expanded="ariaExpanded"
      :aria-label="trimmedAriaLabel"
      :aria-labelledby="
        trimmedAriaLabel || !hasVisibleAccessibleLabel ? undefined : labelId
      "
      class="tw:group tw:cursor-pointer tw:relative tw:inline-flex tw:h-5 tw:w-10 tw:shrink-0 tw:appearance-none tw:items-center tw:justify-center tw:rounded-full tw:border-0 tw:bg-transparent tw:p-0 tw:focus-visible:outline-2 tw:focus-visible:outline-offset-2"
      data-testid="base-toggle-button"
      :disabled="disabled"
      role="switch"
      type="button"
    >
      <span
        aria-hidden="true"
        class="tw:pointer-events-none tw:absolute tw:size-full tw:rounded-md"
      ></span>
      <span
        aria-hidden="true"
        class="tw:pointer-events-none tw:absolute tw:mx-auto tw:h-4 tw:w-9 tw:rounded-full tw:transition-colors tw:duration-200 tw:ease-in-out"
        :class="modelValue ? activeBackgroundClass : inactiveBackgroundClass"
      ></span>
      <span
        aria-hidden="true"
        class="tw:pointer-events-none tw:absolute tw:left-0 tw:inline-block tw:size-5 tw:transform tw:rounded-full tw:border tw:border-denim-200 tw:bg-white tw:shadow-sm tw:ring-0 tw:transition-transform tw:duration-200 tw:ease-in-out"
        :class="modelValue ? 'tw:translate-x-5' : 'tw:translate-x-0'"
      ></span>
    </button>

    <div
      v-if="hasVisibleAccessibleLabel"
      :id="labelId"
      class="tw:text-wrap"
      :class="labelOrder === 'first' ? 'tw:order-first' : 'tw:order-last'"
      data-testid="base-toggle-label-container"
      :style="labelStyle"
    >
      <slot
        :disabled="disabled"
        :label="label"
        name="label"
      >
        <BaseLineClamp
          data-testid="base-toggle-line-clamp"
          :lines="1"
          @clamp-changed="(val) => (isClamped = val)"
        >
          {{ label }}
        </BaseLineClamp>
      </slot>
    </div>

    <BaseStreemTooltip
      v-if="label && isClamped"
      data-testid="base-toggle-tooltip"
    >
      {{ label }}
    </BaseStreemTooltip>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  type CSSProperties,
  ref,
  useAttrs,
  useId,
  useSlots,
} from "vue";

import { BaseLineClamp, BaseStreemTooltip } from "shared/components/base";
import { type Maybe, type Nullable } from "shared/types";

defineOptions({ inheritAttrs: false });

const modelValue = defineModel<boolean>();

const props = withDefaults(
  defineProps<{
    activeBackgroundClass?: string;
    ariaLabel?: Nullable<string>;
    disabled?: boolean;
    inactiveBackgroundClass?: string;
    label?: Nullable<string>;
    labelMaxWidth?: number;
    labelOrder?: "first" | "last";
  }>(),
  {
    activeBackgroundClass: "tw:bg-ai-secondary",
    ariaLabel: null,
    inactiveBackgroundClass: "tw:bg-denim-200",
    label: null,
    labelMaxWidth: undefined,
    labelOrder: "last",
  }
);

const attrs = useAttrs();
const slots = useSlots();
const labelId = useId();

const trimmedAriaLabel = computed<Maybe<string>>(
  () => props.ariaLabel?.trim() || undefined
);

const labelStyle = computed<CSSProperties>(() => {
  if (props.labelMaxWidth) {
    return {
      maxWidth: `${props.labelMaxWidth}px`,
    };
  }

  return {};
});

const hasVisibleAccessibleLabel = computed<boolean>(() =>
  Boolean(props.label?.trim() || slots.label)
);

const isClamped = ref<boolean>(false);

const ariaControls = computed<Maybe<string>>(() => {
  const value = attrs["aria-controls"];

  return typeof value === "string" ? value : undefined;
});

const ariaExpanded = computed<Maybe<boolean>>(() => {
  const value = attrs["aria-expanded"];

  if (typeof value === "boolean") return value;

  if (value === "true") return true;

  if (value === "false") return false;

  return undefined;
});

const wrapperAttrs = computed(() => {
  const {
    "aria-controls": ariaControlsAttr,
    "aria-expanded": ariaExpandedAttr,
    ...rest
  } = attrs;

  return rest;
});

function toggle(): void {
  if (props.disabled) {
    return;
  }

  modelValue.value = !modelValue.value;
}
</script>
